import { Gauge } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { TeamWorkloadEntry } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useTeamWorkload } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip, type ChipColor } from '../chip';

/** Load tiers: 1–2 low, 3–5 medium, 6+ overloaded; 0 = idle. */
function tier(n: number): { color: ChipColor; bar: string } {
  if (n >= 6) return { color: 'red', bar: '#ef4444' };
  if (n >= 3) return { color: 'amber', bar: '#f59e0b' };
  if (n >= 1) return { color: 'green', bar: '#10b981' };
  return { color: 'gray', bar: '#6b7280' };
}

/** A bar reaching full width at ~8 open vacancies keeps the tiers visually distinct. */
const BAR_FULL_AT = 8;

function WorkloadRow({ m }: { m: TeamWorkloadEntry }) {
  const formatName = useEmployeeNameFormatter();
  const { color, bar } = tier(m.openVacancies);
  const pct = Math.min(100, (m.openVacancies / BAR_FULL_AT) * 100);
  const initials = `${m.firstName?.[0] ?? ''}${m.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <div className="dashboard-row flex items-center gap-3 border-b border-border py-2.5 last:border-0">
      <Link
        to="/employees/$id"
        params={{ id: m.id }}
        className="group flex w-36 shrink-0 items-center gap-2.5"
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={m.avatarUrl ?? undefined} />
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-sm font-medium group-hover:underline">
          {formatName(m)}
        </span>
      </Link>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: bar }} />
      </div>
      <Chip color={color}>{m.openVacancies} open</Chip>
    </div>
  );
}

/**
 * `teamWorkload` widget — the recruitment lead's direct reports, each with a
 * color-coded load bar + count of the ACTIVE vacancies they recruit on, busiest first.
 * Names link to the profile.
 */
export function TeamWorkloadWidget() {
  const { data, isLoading, isError } = useTeamWorkload();
  const rows = data ?? [];

  return (
    <WidgetCard title="My Team Workload" icon={Gauge}>
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-5 w-14" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load your team&apos;s workload.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No team members assigned yet.</p>
      ) : (
        <>
          <ExpandableList
            items={rows}
            getKey={(m) => m.id}
            renderItem={(m: TeamWorkloadEntry) => <WorkloadRow m={m} />}
          />
          <p className="mt-3 text-xs text-muted-foreground">
            🟢 1–2 low · 🟡 3–5 medium · 🔴 6+ overloaded
          </p>
        </>
      )}
    </WidgetCard>
  );
}
